import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  StarIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/solid';

const TestimonialsSection = () => {
  const { t, i18n } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [settings, setSettings] = useState({
    section_title: t('testimonials.title'),
    section_subtitle: t('testimonials.subtitle')
  });
  const [loading, setLoading] = useState(true);

  // Fetch testimonials and settings
  useEffect(() => {
    const fetchData = async () => {
      const isTamil = i18n.language === 'ta';

      // If Tamil, use static content from ta.json
      if (isTamil) {
        const tamilTestimonials = t('testimonials.list', { returnObjects: true });
        if (Array.isArray(tamilTestimonials)) {
          // Map to include images since user didn't provide them
          const mappedTamil = tamilTestimonials.map((t, i) => ({
            ...t,
            id: `ta_${i}`,
            rating: 5,
            image_url: i % 2 === 0 ? 'img/testimonial/9.webp' : 'img/testimonial/16.png',
            image: i % 2 === 0 ? 'img/testimonial/9.webp' : 'img/testimonial/16.png',
            loan_type: t.loanType
          }));
          setTestimonials(mappedTamil);
          setSettings({
            section_title: t('testimonials.title'),
            section_subtitle: t('testimonials.subtitle')
          });
          setLoading(false);
          return;
        }
      }

      try {
        setLoading(true);
        const [testimonialsRes, settingsRes] = await Promise.all([
          axios.get('/api/testimonials/active'),
          axios.get('/api/testimonials/settings')
        ]);

        if (testimonialsRes.data.success && testimonialsRes.data.data.length > 0) {
          setTestimonials(testimonialsRes.data.data);
        } else {
          // Fallback to static data if API returns empty
          setTestimonials([
            {
              id: 1,
              name: 'Rajesh Kumar',
              location: 'Mumbai, Maharashtra',
              rating: 5,
              text: 'AHAM made my dream of owning a home come true! Their no-income-proof process was exactly what I needed as a freelance consultant. The team was supportive throughout.',
              image: 'img/testimonial/9.webp',
              image_url: 'img/testimonial/9.webp',
              loanType: 'Home Construction Loan',
              loan_type: 'Home Construction Loan'
            },
            {
              id: 2,
              name: 'Priya Sharma',
              location: 'Bangalore, Karnataka',
              rating: 5,
              text: 'Excellent service and quick processing! I got my home loan approved in just 10 days. The interest rates were competitive and the staff was very professional.',
              image: 'img/testimonial/16.png',
              image_url: 'img/testimonial/16.png',
              loanType: 'Plot + Construction Loan',
              loan_type: 'Plot + Construction Loan'
            }
          ]);
        }

        if (settingsRes.data.success && settingsRes.data.data) {
          setSettings({
            section_title: settingsRes.data.data.section_title,
            section_subtitle: settingsRes.data.data.section_subtitle
          });
        }
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        // Load static data on error
        setTestimonials([
          {
            id: 1,
            name: 'Rajesh Kumar',
            location: 'Mumbai, Maharashtra',
            rating: 5,
            text: 'AHAM made my dream of owning a home come true! Their no-income-proof process was exactly what I needed as a freelance consultant. The team was supportive throughout.',
            image: 'img/testimonial/9.webp',
            image_url: 'img/testimonial/9.webp',
            loan_type: 'Home Construction Loan'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [t, i18n.language]);

  // Auto-advance testimonials
  useEffect(() => {
    // Reset index if it becomes out of bounds
    if (currentIndex >= testimonials.length && testimonials.length > 0) {
      setCurrentIndex(0);
    }

    const timer = setInterval(() => {
      if (testimonials.length > 0) {
        setCurrentIndex((prev) => {
          if (prev >= testimonials.length) return 0;
          return (prev + 1) % testimonials.length;
        });
      }
    }, 5000);

    return () => clearInterval(timer);
  }, [testimonials.length, currentIndex]);

  // Reset index when testimonials change (language switch)
  useEffect(() => {
    setCurrentIndex(0);
  }, [testimonials.length]); // Use length as trigger for safer reset

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    }
  };

  return (
    <section className="py-20 bg-gradient-light relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary-600 rounded-full mix-blend-multiply filter blur-xl animate-float"></div>
        <div className="absolute top-3/4 right-1/4 w-64 h-64 bg-purple-600 rounded-full mix-blend-multiply filter blur-xl animate-float" style={{ animationDelay: '3s' }}></div>
      </div>

      <div className="container mx-auto px-4 relative">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={containerVariants}
          className="text-center mb-16"
        >
          <motion.h2
            variants={itemVariants}
            className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
          >
            {settings.section_title}
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {settings.section_subtitle}
          </motion.p>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="max-w-6xl mx-auto"
        >
          {!loading && testimonials.length > 0 ? (
            <div className="relative">
              {/* Main Testimonial */}
              <div className="bg-white rounded-3xl shadow-large p-8 lg:p-12 relative overflow-hidden">
                {/* Quote Icon */}
                <div className="absolute top-8 left-8 text-primary-200 text-6xl font-serif">
                  "
                </div>

                <div className="relative z-10">
                  <div className="flex flex-col lg:flex-row items-center lg:items-start space-y-6 lg:space-y-0 lg:space-x-8">
                    {/* Profile */}
                    <div className="flex-shrink-0 text-center lg:text-left">
                      <div className="w-24 h-24 mx-auto lg:mx-0 mb-4 relative">
                        <img
                          src={testimonials[currentIndex]?.image_url || testimonials[currentIndex]?.image}
                           alt={`Testimonial from ${testimonials[currentIndex]?.name || 'Customer'} - AHAM Housing`}
                          className="w-full h-full rounded-full object-cover"
                        />
                        <div className="absolute -bottom-2 -right-2 bg-primary-600 rounded-full p-2">
                          <StarIcon className="h-4 w-4 text-white" />
                        </div>
                      </div>

                      <h4 className="text-xl font-bold text-gray-900 mb-1">
                        {testimonials[currentIndex]?.name}
                      </h4>

                      <p className="text-gray-600 text-sm mb-3">
                        {testimonials[currentIndex]?.location}
                      </p>

                      <div className="inline-block bg-primary-50 text-primary-600 px-3 py-1 rounded-full text-xs font-medium">
                        {testimonials[currentIndex]?.loan_type || testimonials[currentIndex]?.loanType}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      {/* Rating */}
                      <div className="flex justify-center lg:justify-start space-x-1 mb-4">
                        {[...Array(testimonials[currentIndex]?.rating || 0)].map((_, i) => (
                          <StarIcon key={i} className="h-5 w-5 text-yellow-400" />
                        ))}
                      </div>

                      {/* Testimonial Text */}
                      <blockquote className="text-lg lg:text-xl text-gray-700 leading-relaxed italic text-center lg:text-left">
                        "{testimonials[currentIndex]?.text}"
                      </blockquote>
                    </div>
                  </div>
                </div>

                {/* Background decoration */}
                <div className="absolute bottom-0 right-0 w-32 h-32 bg-primary-50 rounded-full transform translate-x-16 translate-y-16"></div>
              </div>

              {/* Navigation */}
              <div className="flex justify-center items-center mt-8 space-x-4">
                <button
                  onClick={prevTestimonial}
                  className="bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-primary-300 text-gray-600 hover:text-primary-600 p-3 rounded-full transition-all duration-300"
                >
                  <ChevronLeftIcon className="h-6 w-6" />
                </button>

                {/* Indicators */}
                <div className="flex space-x-2">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentIndex
                        ? 'bg-primary-600 scale-125'
                        : 'bg-gray-300 hover:bg-primary-400'
                        }`}
                    />
                  ))}
                </div>

                <button
                  onClick={nextTestimonial}
                  className="bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-primary-300 text-gray-600 hover:text-primary-600 p-3 rounded-full transition-all duration-300"
                >
                  <ChevronRightIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Thumbnail Row */}
              <div className="hidden lg:flex justify-center mt-8 space-x-4">
                {testimonials.map((testimonial, index) => (
                  <button
                    key={testimonial.id}
                    onClick={() => setCurrentIndex(index)}
                    className={`flex-shrink-0 p-2 rounded-2xl transition-all duration-300 ${index === currentIndex
                      ? 'bg-primary-100 border-2 border-primary-300'
                      : 'bg-white border-2 border-transparent hover:bg-gray-50'
                      }`}
                  >
                    <div className="flex items-center space-x-3">
                      <img
                        src={testimonial.image_url || testimonial.image}
                          alt={`${testimonial.name} - Testimonial Thumbnail`}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="text-left">
                        <div className="text-sm font-semibold text-gray-900">
                          {testimonial.name}
                        </div>
                        <div className="text-xs text-gray-600">
                          {testimonial.location}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;